import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Calendar,
  BarChart3,
  ChefHat,
  BookOpen,
} from "lucide-react";
import { colors } from "../../../constants/colors";

const SubscriptionDailyUsageSection = ({ uid }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSubscriptionDailyUsage = async (date) => {
    setLoading(true);
    setError(null);
    try {
      let token = sessionStorage.getItem("adminToken");
      if (!token) {
        token = localStorage.getItem("authToken");
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/users/${uid}/subscription-daily-usage/${date}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch subscription daily usage");
      }

      const data = await response.json();
      setUsageData(data.subscriptionDailyUsage);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching subscription daily usage:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (isExpanded) {
      fetchSubscriptionDailyUsage(date);
    }
  };

  const handleToggle = () => {
    if (!isExpanded) {
      fetchSubscriptionDailyUsage(selectedDate);
    }
    setIsExpanded(!isExpanded);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "0.5rem 0",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const countCardStyle = {
    backgroundColor: colors.background,
    borderRadius: "8px",
    padding: "1.5rem",
    textAlign: "center",
    border: `1px solid ${colors.lightGray}`,
    transition: "all 0.3s ease",
    cursor: "default",
  };

  const countNumberStyle = {
    fontSize: "2rem",
    fontWeight: "700",
    marginBottom: "0.5rem",
  };

  const countLabelStyle = {
    fontSize: "0.875rem",
    fontWeight: "500",
    color: colors.darkGray,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={handleToggle}>
        <div style={titleStyle}>
          <BarChart3 size={20} />
          Meal & Recipe Counts
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {isExpanded ? (
            <EyeOff size={16} color={colors.darkGray} />
          ) : (
            <Eye size={16} color={colors.darkGray} />
          )}
          {isExpanded ? (
            <ChevronUp size={20} color={colors.darkGray} />
          ) : (
            <ChevronDown size={20} color={colors.darkGray} />
          )}
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginTop: "1rem" }}>
          {/* Date Picker */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "600",
                color: colors.darkBlue,
                fontSize: "0.875rem",
              }}
            >
              <Calendar
                size={16}
                style={{ marginRight: "0.5rem", verticalAlign: "middle" }}
              />
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              style={{
                padding: "0.75rem",
                border: `1px solid ${colors.lightGray}`,
                borderRadius: "8px",
                fontSize: "1rem",
                backgroundColor: colors.white,
                color: colors.darkBlue,
                cursor: "pointer",
                width: "200px",
              }}
            />
          </div>

          {/* Loading State */}
          {loading && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: colors.darkGray,
              }}
            >
              Loading subscription daily usage...
            </div>
          )}

          {/* Error State */}
          {error && (
            <div
              style={{
                backgroundColor: "#fee",
                color: "#c33",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                border: "1px solid #fcc",
              }}
            >
              Error: {error}
            </div>
          )}

          {/* Usage Data */}
          {usageData && !loading && (
            <div>
              <div
                style={{
                  marginBottom: "1.5rem",
                  padding: "1rem",
                  backgroundColor: colors.lightBlue,
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: colors.darkBlue,
                    fontSize: "1.1rem",
                    fontWeight: "600",
                  }}
                >
                  {formatDate(selectedDate)}
                </h3>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                {/* Meal Count Card */}
                <div style={countCardStyle}>
                  <div style={{ marginBottom: "1rem" }}>
                    <ChefHat size={32} color={colors.primary} />
                  </div>
                  <div
                    style={{
                      ...countNumberStyle,
                      color: colors.primary,
                    }}
                  >
                    {usageData.mealCount || 0}
                  </div>
                  <div style={countLabelStyle}>Meals Generated</div>
                </div>

                {/* Recipe Count Card */}
                <div style={countCardStyle}>
                  <div style={{ marginBottom: "1rem" }}>
                    <BookOpen size={32} color={colors.secondary} />
                  </div>
                  <div
                    style={{
                      ...countNumberStyle,
                      color: colors.secondary,
                    }}
                  >
                    {usageData.recipeCount || 0}
                  </div>
                  <div style={countLabelStyle}>Recipes Generated</div>
                </div>
              </div>

              {/* Summary */}
              {(usageData.mealCount > 0 || usageData.recipeCount > 0) && (
                <div
                  style={{
                    marginTop: "1.5rem",
                    padding: "1rem",
                    backgroundColor: colors.lightGreen,
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: colors.darkGreen,
                    }}
                  >
                    Total Activity:{" "}
                    {(usageData.mealCount || 0) + (usageData.recipeCount || 0)}{" "}
                    items generated
                  </div>
                </div>
              )}

              {/* No Activity Message */}
              {!usageData.mealCount && !usageData.recipeCount && (
                <div
                  style={{
                    marginTop: "1.5rem",
                    padding: "2rem",
                    textAlign: "center",
                    color: colors.darkGray,
                    backgroundColor: colors.lightGray,
                    borderRadius: "8px",
                  }}
                >
                  <BarChart3
                    size={32}
                    color={colors.darkGray}
                    style={{ marginBottom: "0.5rem" }}
                  />
                  <div>No meals or recipes generated on this date</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionDailyUsageSection;
